import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';

export function taxStripeKeyMatches(key, tax) {
  return !tax || (tax.mode === 'test' ? /^pk_test_/.test(key || '') : tax.mode === 'live' && /^pk_live_/.test(key || ''));
}

export function validTrialTaxQuote(value, tax) {
  return !!value && [value.subtotal, value.tax, value.total].every(Number.isSafeInteger) &&
    /^[a-z]{3}$/i.test(value.currency || '') && typeof value.priceId === 'string' && !!value.priceId &&
    value.behavior === tax?.behavior && /^[a-f0-9]{64}$/.test(value.basis || '');
}

export function nativePaymentLocation(event) {
  const address = [event?.value?.billingDetails?.address, event?.value?.payment_method?.billing_details?.address]
    .find(value => /^[a-z]{2}$/i.test(value?.country || ''));
  if (!/^[a-z]{2}$/i.test(address?.country || '')) return null;
  const postalCode = address.postalCode ?? address.postal_code;
  if (postalCode != null && (typeof postalCode !== 'string' || postalCode.length > 32 || /[\u0000-\u001f\u007f]/.test(postalCode))) return null;
  return { country: address.country.toUpperCase(), ...(postalCode?.trim() ? { postalCode: postalCode.trim().toUpperCase() } : {}) };
}

// No address UI or second review button. Stripe supplies an optional location
// hint; the server verifies the actual saved payment method before activation.
export function useTrialTax({ automaticTax, planKey, paymentMethodId, initialQuote = null, disabled = false }) {
  const { t } = useTranslation();
  const [location, setLocation] = useState(null);
  const key = JSON.stringify([automaticTax?.mode, automaticTax?.behavior, planKey || automaticTax?.defaultPlanKey, paymentMethodId || location]);
  const current = useRef(key); current.current = key;
  const disabledRef = useRef(disabled); disabledRef.current = disabled;
  const revision = useRef(0);
  const [reviewed, setReviewed] = useState(() => validTrialTaxQuote(initialQuote, automaticTax) ? { key, value: initialQuote } : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const quote = reviewed?.key === key ? reviewed.value : null;
  useEffect(() => {
    const requestRevision = ++revision.current;
    setError(null);
    if (!automaticTax || (!paymentMethodId && !location)) { setBusy(false); return; }
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.billing.previewTrialTax({ planKey, ...(paymentMethodId ? { paymentMethodId } : { nativeLocation: location }) });
        const value = response?.data ?? response;
        if (!validTrialTaxQuote(value, automaticTax)) throw new Error(t('trial.taxError', 'We could not calculate tax. Check the billing details in Stripe.'));
        if (revision.current === requestRevision && current.current === key) setReviewed({ key, value });
      } catch (err) {
        if (revision.current === requestRevision && current.current === key) {
          setReviewed(null);
          setError(err?.message || t('trial.taxError', 'We could not calculate tax. Check the billing details in Stripe.'));
        }
      } finally { if (revision.current === requestRevision) setBusy(false); }
    }, paymentMethodId ? 0 : 350);
    return () => { clearTimeout(timer); revision.current++; };
    // The key includes all quote inputs, including primitive native location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  function updateQuote(value) {
    revision.current++;
    setBusy(false);
    setReviewed(validTrialTaxQuote(value, automaticTax) ? { key: current.current, value } : null);
  }
  return {
    quote, busy, error,
    activation: automaticTax ? { taxQuote: quote } : {},
    updateQuote,
    onPaymentChange(event) {
      if (!automaticTax || disabledRef.current) return;
      const next = nativePaymentLocation(event);
      setLocation(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    },
  };
}
